import { incomeTaxYear } from "@stb/jurisdiction-au/income-tax"

import { displayYear } from "../../components/home/home-content"
import { formatAud, SimpleTable, SourceLine } from "../../components/landing"
import { PayCalculator } from "../../components/landing/pay-calculator"
import {
  DEFAULT_FY,
  studyLoanRange,
  studyLoanRule,
  variantAnswer,
} from "../../components/landing/pay-calculator-model"
import type { Guide } from "../guide-types"
import { hecsGuide } from "../hecs-repayment-calculator"
import { englishDateToZh } from "./dates"

/**
 * Chinese counterpart of content/hecs-repayment-calculator.tsx (T1320). Same engine, thresholds, sources and dates;
 * only the words differ. Terms: docs/design/landing/copy-zh.md.
 */
const year = incomeTaxYear(DEFAULT_FY)
const fy = displayYear(DEFAULT_FY)
const source = year.studyLoan.source
const checked = englishDateToZh(hecsGuide.checked)

const dollars = (n: number) => `A$${n.toLocaleString("en-AU")}`

const bands = year.studyLoan.bands
const bandRows = bands.map((b, k) => ({
  header: studyLoanRange(bands, k, "zh"),
  cells: [studyLoanRule(b, "zh")],
}))
const minimum = dollars(bands.find((b) => b.rateBp > 0)?.from ?? 0)

export const hecsGuideZh: Guide = {
  slug: hecsGuide.slug,
  title: "HECS-HELP 还款计算器",
  description: `按收入算出 ${fy} 财年的 HECS-HELP（高等教育贷款计划）强制还款额。可看每年、每两周或每周的金额，附 ATO 门槛和到手收入，免费使用。`,
  crumb: "HECS-HELP 还款计算器",
  h1: `HECS-HELP 还款计算器（${fy} 财年）`,
  published: hecsGuide.published,
  reviewed: hecsGuide.reviewed,
  badge: "免费工具",
  financialYear: fy,
  checked,
  sources: [{ label: "ATO，学生和培训贷款的还款收入及费率", href: source.url }],
  answer: variantAnswer("help", formatAud, "zh"),
  answerMore: (
    <>
      计算器把 ATO {fy} 财年的还款费率用在你的应税收入上，并把还款额与所得税、Medicare
      levy（医疗保险税）和到手收入放在一起显示。请在下面填入你自己的收入。
    </>
  ),
  related: [
    { label: "工资计算器：税后到手收入", href: "/pay-calculator" },
    {
      label: "Medicare levy 计算器：医疗保险税与低收入减免",
      href: "/medicare-levy-calculator",
    },
    { label: "Cents per km 计算器：算出你的汽车开支扣除额", href: "/cents-per-km-calculator" },
    { label: "个体经营者开发票：发出发票，看谁已付款", href: "/invoicing" },
  ],
  toc: [
    { id: "calculator", label: "算出你的还款额" },
    { id: "thresholds", label: `${fy} 财年还款门槛` },
    { id: "how", label: "还款是怎么运作的" },
    { id: "faq", label: "常见问题" },
  ],
  cta: {
    title: "自雇又有 HELP 债务？把账放在一处",
    description:
      "你的还款额在报税时才算，所以 ABN 收入需要提前规划。Kestly 把发票、收据和行程放在一处，报税时已经备好。",
    ctaLabel: "免费开始",
    ctaHref: "/login?from=hecs-repayment-calculator",
  },
  body: (
    <>
      <section aria-labelledby="calculator" className="grid gap-4">
        <h2 id="calculator" className="text-2xl font-semibold">
          算出你的还款额
        </h2>
        <PayCalculator variant="help" />
      </section>

      <section aria-labelledby="thresholds" className="grid gap-3">
        <h2 id="thresholds" className="text-2xl font-semibold">
          {fy} 财年 HELP 还款门槛
        </h2>
        <p className="max-w-[70ch]">
          还款收入超过 {minimum}{" "}
          之前，不用还款。超过之后，按你的收入落在哪一档来决定费率。HELP（HECS-HELP、FEE-HELP、SA-HELP、OS-HELP）、VET
          学生贷款和其他学生及培训贷款共用同一笔还款。
        </p>
        <SimpleTable
          caption={`${fy} 财年 HELP 还款门槛`}
          columns={["还款收入", `强制还款（${fy} 财年）`]}
          rows={bandRows}
        />
        <SourceLine
          sources={[{ label: "ATO", href: source.url }]}
          financialYear={fy}
          checked={checked}
        />
      </section>

      <section aria-labelledby="how" className="grid max-w-[70ch] gap-3">
        <h2 id="how" className="text-2xl font-semibold">
          HELP 还款是怎么运作的
        </h2>
        <p>
          还款不会作为单独的账单从你的工资里扣。如果你告诉雇主你有学生贷款，雇主每期会多预扣一些税；你报税时，ATO
          再算出实际金额，已预扣的部分计入其中。
        </p>
        <p>
          ATO
          用的是你的还款收入，即应税收入加上某些其他项目，例如应申报的附加福利和净投资亏损。本计算器用的是你的应税收入（除非你勾选，否则不计入标准扣除额），所以如果你有这些项目，实际数字可能不同。
        </p>
        <p>
          自愿还款不包括在内。你的债务每年 6 月 1
          日还会按指数调整，这会改变你欠的总额，但不改变这里显示的强制还款额。
        </p>
      </section>
    </>
  ),
  faq: [
    {
      question: "今年的 HELP 还款门槛是多少？",
      answer: `${fy} 财年的最低还款收入是 ${minimum}。低于这个数，不用强制还款。完整的表格在上方，来自 ATO。`,
    },
    {
      question: "HECS 和 HELP 是一回事吗？",
      answer:
        "HECS-HELP 是 HELP 贷款的一种，另外还有 FEE-HELP、SA-HELP 和 OS-HELP。VET 学生贷款和其他学生及培训贷款是另外的贷款，但它们共用一笔按收入计算的强制还款。",
    },
    {
      question: "我的还款额是怎么算的？",
      answer:
        "ATO 看你当年的还款收入，按它所在那一档的费率计算。各档在上方的表格里，计算器会把它们用在你的收入上。",
    },
    {
      question: "还款会减少我的到手收入吗？",
      answer:
        "会，只要你告诉了雇主你有学生贷款。雇主每期会为此多预扣税。计算器把还款额单独列成一行，放在到手收入之前。",
    },
    {
      question: "什么算还款收入？",
      answer:
        "应税收入，加上应申报的附加福利、应申报的 super 缴费、净投资亏损和部分免税的外国收入。本计算器只用应税收入。",
    },
    {
      question: "我可以提前还清 HELP 债务吗？",
      answer: "可以。你随时可以向 ATO 自愿还款。本计算器只显示强制还款额。",
    },
    {
      question: "我住在国外还要还款吗？",
      answer:
        "要。如果你在国外居住超过六个月，必须向 ATO 申报来自所有来源的收入，并按同样的办法还款。本计算器不涵盖这种情况。",
    },
    {
      question: "我用 ABN 赚钱怎么办？",
      answer:
        "还款额仍然在你报税时按收入计算，但你赚钱时没有税款被预扣，所以要自己留出这笔钱。受雇收入请用工资计算器，ABN 记录另外保管。",
    },
    {
      question: "ATO 每年都会改门槛吗？",
      answer:
        "会。门槛每个财年都会更新。ATO 公布后我们会更新计算器，并在表格旁显示我们最近核对的日期。",
    },
  ],
}
